/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */

import store from 'core/store';

export default function queueFactory(options) {
    const queue = [];
    const { id, bufferSize, flush } = options;

    const queueInstance = {
        get id() {
            return id;
        },
        get size() {
            return queue.length;
        },
        /**
         * Gets queue store
         * @returns {Promise<Store>}
         */
        getStore() {
            return store('messageQueue', store.backends.sessionStorage);
        },
        /**
         * Enqueue message
         * @param {any[]} message
         * @returns {Promise}
         */
        enqueue(message) {
            if (Array.isArray(message)) {
                queue.push(...message);
            } else {
                queue.push(message);
            }

            if (this.size >= bufferSize) {
                return this.flush();
            }
            return this.backup();
        },
        /**
         * Flushes queue and call flush action function provided during queue creation
         * @returns {Promise}
         */
        flush() {
            if (this.size === 0) {
                return Promise.resolve();
            }
            const messages = queue.splice(0, this.size);

            /**
             * Chunks the queue by bufferSize, so it can be flushed in multiple smaller parts
             * @param {any[]} toBeChunked
             * @returns {any[][]}
             */
            const chunkMessages = (toBeChunked = []) => {
                const chunks = [];
                while (toBeChunked.length) {
                    chunks.push(toBeChunked.splice(0, bufferSize));
                }
                return chunks;
            };

            return Promise.allSettled(
                chunkMessages(messages)
                    .map(chunk => flush(chunk)
                        .catch(() => {
                            // If a single flushed chunk fails, put that chunk back in the queue
                            queue.unshift(...chunk);
                        })
                    )
            ).then(() => this.backup());
        },
        /**
         * Saves queue in browser storage
         * @returns {Promise}
         */
        backup() {
            return this.getStore().then(messageStore => {
                if (this.size === 0) {
                    return messageStore.removeItem(this.id);
                }
                return messageStore.setItem(this.id, JSON.stringify(queue));
            });
        },
        /**
         * Restores queue from browser storage
         * @returns {Promise}
         */
        restore() {
            return this.getStore()
                .then(messageStore => messageStore.getItem(this.id))
                .then(storedQueue => {
                    if (storedQueue) {
                        queue.unshift(...JSON.parse(storedQueue));
                    }
                });
        }
    };

    return queueInstance.restore().then(() => queueInstance);
}
